import { useQuery } from '@tanstack/react-query';
import { fetchLondonState, type LondonState } from '../api/londonApi';
import { londonKeys } from './queries';

/**
 * Лисёнок и его состояние вне игры — на главной и во вкладке «Выживи в Лондоне» (решение владельца 2026-11-02:
 * «вывести его атрибуты на главный экран: деньги, состояние и прочее»; 2026-11-03: «главную как обсуждали — маскот,
 * состояние игры»). Только состояние сервера, без содержания сезона: непрочитанное считается по `available − seen`.
 */

/** Кто пишет в телефон — имена для строки «Новые сообщения». */
const CHAT_NAMES: Record<string, string> = {
  gran: 'Бабушка',
  mum: 'Мама',
  dad: 'Папа',
  kai: 'Кай',
  mia: 'Мия',
  sam: 'Сэм',
  olly: 'Олли',
  arthur: 'Артур',
  nora: 'Нора',
};

/** Лицо лисёнка — ячейка листа `fox-faces` (как FOX_FACE в ui/sprites.tsx). */
export const FOX_MOOD = { happy: 0, confused: 1, surprised: 2, sad: 3, thinking: 4, tired: 5 } as const;

export interface FoxStatus {
  /** Ячейка листа `fox-faces`. */
  face: number;
  /** Строка о лисёнке — заголовок карточки. */
  title: string;
  /** Пояснение под ней. */
  text: string;
  /** Новая игра: ещё не видел интро. */
  fresh: boolean;
}

/** Непрочитанные чаты — имена в порядке телефона. */
export function unreadChats(state: Pick<LondonState, 'chats'>): string[] {
  return Object.entries(state.chats)
    .filter(([, c]) => (c.available ?? 0) > c.seen)
    .map(([id]) => CHAT_NAMES[id] ?? id);
}

/** Что сейчас с лисёнком — по важности: новая игра, устал, голоден, ждут ответа, всё хорошо. Без рода об игроке. */
export function foxStatus(state: LondonState): FoxStatus {
  const unread = unreadChats(state);
  if (!state.intro_seen) {
    return {
      face: FOX_MOOD.happy,
      title: `${state.hero_name || 'Лисёнок'} собирается в Лондон`,
      text: 'Бабушка просит найти её старую чайную «Медный чайник». Говори с жителями по-английски — голосом или текстом.',
      fresh: true,
    };
  }
  if (state.energy.left <= 0) {
    return {
      face: FOX_MOOD.tired,
      title: 'Силы на сегодня кончились',
      text: 'Силы на разговоры вернутся завтра. Пока можно читать сообщения, готовить и гулять.',
      fresh: false,
    };
  }
  if (state.hunger < 30) {
    return { face: FOX_MOOD.sad, title: 'Пора перекусить', text: 'Загляни в закусочную к Олли или в кафе к Сэм.', fresh: false };
  }
  if (unread.length) {
    return {
      face: FOX_MOOD.surprised,
      title: unread.length === 1 ? `${unread[0]}: новое сообщение` : 'Новые сообщения',
      text: unread.length === 1 ? 'Открой телефон и ответь по-английски.' : `${unread.join(', ')} — открой телефон и ответь.`,
      fresh: false,
    };
  }
  return { face: FOX_MOOD.happy, title: `${state.hero_name || 'Лисёнок'} в Лондоне`, text: 'Тайна «Медного чайника» ждёт.', fresh: false };
}

export function formatPence(p: number): string {
  return '£' + (p / 100).toFixed(2);
}

/**
 * Состояние игры для карточек вне игры. Тот же кэш, что у самой игры. Нет доступа (новая игра пока у админов и
 * тестовых учёток) — `denied`: на главной и во вкладке остаётся прежняя игра. Сбой запроса — `failed`, только если
 * показать нечего: не удалось лишь фоновое обновление — остаётся последнее состояние.
 */
export function useLondonSummary() {
  const q = useQuery({ queryKey: londonKeys.state, queryFn: fetchLondonState, retry: false, staleTime: 15_000 });
  const status = (q.error as { response?: { status?: number } } | null)?.response?.status;
  return {
    state: q.data ?? null,
    loading: q.isPending,
    denied: q.isError && (status === 403 || status === 404),
    failed: q.isError && !q.data && status !== 403 && status !== 404,
    refetch: q.refetch,
  };
}
